import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import PersonalInformation from "./components/UserPersonalInformation";
import ClinicInformation from "./components/UserClinicInformation";
import HRInformation from "./components/UserHrInformation";
import Availability from "./components/UserAvailabilityInformation";
import { logInfo } from "../../../src/utils/logger";
import { fetchUsers } from "../../store/thunks/userThunk";

const UserDetails = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("personal");
  const { status, error, selectedUser } = useSelector(
    (state) => state.users
  );
  const dispatch = useDispatch();

  useEffect(() => {
    logInfo("UserDetails component rendered", {
      userId: selectedUser?._id,
      activeTab,
      componentName: "UserDetails",
    });
  }, [selectedUser, activeTab]);

  const tabs = [
    {
      id: "personal",
      name: "Personal",
      component: PersonalInformation,
    },
    { id: "hr", name: "HR Info", component: HRInformation },
    {
      id: "clinician",
      name: "Clinics",
      component: ClinicInformation,
    },
    { id: "availability", name: "Availability", component: Availability },
  ];

  const ActiveTabComponent = tabs.find((tab) => tab.id === activeTab)?.component;

  const handleUserUpdate = () => {
    dispatch(fetchUsers());
  };

  if (status === "loading") return (
    <div className="flex items-center justify-center h-64">
      <div className="text-sm text-gray-600">Loading...</div>
    </div>
  );
  
  if (status === "failed") return (
    <div className="flex items-center justify-center h-64">
      <div className="text-sm text-red-600">Error: {error}</div>
    </div>
  );
  
  if (!selectedUser) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-sm text-gray-600">No user selected</div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-6 bg-white border border-gray-200 rounded-sm shadow-sm">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">User Details</h1>
          <p className="text-sm text-gray-600 mt-1">
            {selectedUser.firstName} {selectedUser.lastName}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-sm transition-colors"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="min-h-[500px]">
        {ActiveTabComponent && (
          <ActiveTabComponent
            user={selectedUser}
            isEditMode={true}
            onComplete={handleUserUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default UserDetails;
